import type { CropMonitorRecord } from '@/types/api';
import NdviBadge from '@/components/feature/NdviBadge';
import ImageGrid from './ImageGrid';

interface LatestDataPanelProps {
  record: CropMonitorRecord;
  totalCnt?: number;
  housingHeightMm?: number;
  isSelected?: boolean;
  onBackToLatest?: () => void;
}

const formatMm = (mm: number) =>
  `${Number(mm).toFixed(0)} mm (${(Number(mm) / 1000).toFixed(2)} m)`;

const formatMmSimple = (mm: number) =>
  `${Number(mm).toFixed(0)} mm`;

function formatCaptureTime(t: string): string {
  if (!t || t.length < 12) return t;
  return (
    t.substring(0, 2) + '.' +
    t.substring(2, 4) + '.' +
    t.substring(4, 6) + ' ' +
    t.substring(6, 8) + ':' +
    t.substring(8, 10) + ':' +
    t.substring(10, 12)
  );
}

export default function LatestDataPanel({
  record,
  totalCnt = 0,
  housingHeightMm = 2200,
  isSelected = false,
  onBackToLatest,
}: LatestDataPanelProps) {
  const title = isSelected
    ? `📸 ${formatCaptureTime(record.capture_time)} 촬영 데이터`
    : '최신 촬영 데이터';

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden border transition-all duration-300"
      style={{ borderColor: isSelected ? '#2E86C1' : '#f0f0f0' }}
    >
      {/* Section Header */}
      <div
        className="px-5 py-3.5 border-b flex items-center gap-2"
        style={{
          backgroundColor: isSelected ? '#EBF5FB' : '#f0f9f4',
          borderColor: isSelected ? '#d0e8f7' : '#f0f0f0',
        }}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i
            className={isSelected ? 'ri-image-2-line' : 'ri-camera-fill'}
            style={{ color: isSelected ? '#2E86C1' : '#27ae60' }}
          ></i>
        </div>
        <span className="font-bold text-gray-800 text-sm flex-1 min-w-0 truncate">{title}</span>

        <div className="flex items-center gap-2 ml-auto flex-shrink-0">
          {isSelected && onBackToLatest && (
            <button
              onClick={onBackToLatest}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-colors"
              style={{ backgroundColor: '#1B4F72', color: '#fff' }}
              title="최신 데이터로 돌아가기"
            >
              <i className="ri-arrow-up-line text-xs"></i>
              최신으로
            </button>
          )}
          <span className="text-xs text-gray-400">촬영 #{record.seq}</span>
        </div>
      </div>

      <div className="p-4 md:p-5">
        <div className="flex flex-col lg:flex-row gap-5">
          {/* Image Grid */}
          <div className="flex-1 min-w-0">
            <ImageGrid record={record} />
          </div>

          {/* Data Info */}
          <div className="lg:w-72 flex-shrink-0">
            <div className="space-y-3">
              <DataRow
                icon="ri-time-line"
                label="촬영 시각"
                value={formatCaptureTime(record.capture_time)}
                color="#1B4F72"
              />
              <DataRow
                icon="ri-hashtag"
                label="촬영 순번"
                value={`#${record.seq} / ${totalCnt}`}
                color="#1B4F72"
              />
              <DataRow
                icon="ri-focus-3-line"
                label="ToF 중앙 거리"
                value={formatMm(record.tof_center_mm)}
                color="#2E86C1"
              />
              <DataRow
                icon="ri-plant-line"
                label="벼 높이"
                value={formatMm(record.rice_height_mm)}
                color="#27ae60"
              />
              <DataRow
                icon="ri-ruler-line"
                label="장비 설치 높이"
                value={formatMmSimple(housingHeightMm)}
                color="#7f8c8d"
              />
              <div
                className="flex items-start gap-3 p-3 rounded-xl"
                style={{ backgroundColor: '#f8fffe' }}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ backgroundColor: '#e8f8f0' }}
                >
                  <i className="ri-leaf-line text-green-600"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 mb-1">NDVI 평균</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-800 text-sm">
                      {Number(record.ndvi_avg).toFixed(4)}
                    </span>
                    <NdviBadge value={Number(record.ndvi_avg)} showValue={false} />
                  </div>
                </div>
              </div>
              <DataRow
                icon="ri-signal-tower-line"
                label="수신 시각"
                value={record.regdate}
                color="#7f8c8d"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DataRow({
  icon,
  label,
  value,
  color,
}: {
  icon: string;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
      <div
        className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
        style={{ backgroundColor: color + '18' }}
      >
        <i className={icon} style={{ color }}></i>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="font-semibold text-gray-800 text-sm mt-0.5 break-all">{value}</p>
      </div>
    </div>
  );
}